import type { MatchNode, Rule } from '@guard-dog/contracts';

/**
 * Build a valid rules-as-data `Rule` from the console's guided form. Scoped to the common cases an
 * operator authors from the UI — block/allow a path (access) or an IP/CIDR (ip); the full match-tree
 * grammar is still authored via the API. The platform's `validateRule` is the authority; this just
 * produces well-formed input for it.
 */
export type RuleKind = 'block-path' | 'allow-path' | 'block-ip' | 'allow-ip' | 'rate-limit-path';

/** Defaults for a new rate-limit rule: the shape the starter pack ships and production runs. */
export const RATE_LIMIT_DEFAULTS = {
  limit: 5,
  windowSec: 60,
  ipLimit: 20,
  then: 'block' as const,
};

export interface RuleFormInput {
  kind: RuleKind;
  /** A URL path (`/xmlrpc.php`, `/wp-admin/*`) or an IP/CIDR (`203.0.113.10`, `198.51.100.0/24`). */
  target: string;
  scope: 'tenant' | 'site';
  tenantId: string;
  /** Required when scope === 'site'. */
  siteId?: string;
  description?: string;
  /** `rate-limit-path` only — attempts allowed per (source, account) in the window. */
  limit?: number;
  /** `rate-limit-path` only — window length in seconds. */
  windowSec?: number;
  /**
   * `rate-limit-path` only — ceiling for one source across all accounts it tries.
   * Left undefined this is per-source-only counting, which charges everyone behind a shared
   * address to a single budget (ADR-0029).
   */
  ipLimit?: number;
  /** `rate-limit-path` only — what exceeding a budget does. Defaults to `block`. */
  then?: 'block' | 'challenge';
}

const RULE_KINDS: RuleKind[] = [
  'block-path',
  'allow-path',
  'block-ip',
  'allow-ip',
  'rate-limit-path',
];

export function isRuleKind(v: string): v is RuleKind {
  return (RULE_KINDS as string[]).includes(v);
}

/** A short, human-ish id so authored rules are recognizable in the store. */
function ruleId(kind: RuleKind): string {
  return `manual-${kind}-${crypto.randomUUID().slice(0, 8)}`;
}

export function buildRule(input: RuleFormInput): Rule {
  const target = input.target.trim();
  const isIp = input.kind === 'block-ip' || input.kind === 'allow-ip';
  const isAllow = input.kind === 'allow-path' || input.kind === 'allow-ip';
  const isRate = input.kind === 'rate-limit-path';
  const scopeId = input.scope === 'site' ? input.siteId : input.tenantId;

  const pathCondition = {
    field: 'path',
    op: (target.includes('*') ? 'glob' : 'equals') as 'glob' | 'equals',
    value: target,
  };

  if (isRate) {
    const limit = input.limit ?? RATE_LIMIT_DEFAULTS.limit;
    const windowSec = input.windowSec ?? RATE_LIMIT_DEFAULTS.windowSec;
    return {
      schemaVersion: 1,
      id: ruleId(input.kind),
      version: 1,
      type: 'rate',
      enabled: true,
      scope: input.scope,
      scopeId,
      // Edge-only: the counter is a Durable Object. The WP plane has nothing to enforce it with, and
      // shipping a rule to a plane that silently ignores it is the shape of bug ADR-0029 exists over.
      planes: ['edge'],
      // Lowest precedence of the authored kinds: an outright block or allow should settle a request
      // before it is merely counted.
      priority: 200,
      action: {
        kind: 'rate_limit',
        limit,
        windowSec,
        // Omitted rather than sent as undefined — an absent ipLimit is a meaningful contract state
        // (per-source-only), and the wire should say exactly that.
        ...(input.ipLimit === undefined ? {} : { ipLimit: input.ipLimit }),
        then: input.then ?? RATE_LIMIT_DEFAULTS.then,
      },
      // Method-scoped to POST deliberately. A path-only rate limit also counts GETs, so limiting
      // `/wp-login.php` would lock someone out for *looking at* the login page six times — the
      // limiter is for submission abuse. Rate-limiting a GET path needs the full match grammar
      // through the API.
      match: {
        all: [{ field: 'method', op: 'equals' as const, value: 'POST' }, pathCondition],
      },
      meta: { source: 'manual', description: input.description?.trim() || undefined },
    };
  }

  const match = isIp
    ? { all: [{ field: 'ip', op: 'cidr' as const, value: [target] }] }
    : { all: [pathCondition] };

  return {
    schemaVersion: 1,
    id: ruleId(input.kind),
    version: 1,
    type: isIp ? 'ip' : 'access',
    enabled: true,
    scope: input.scope,
    scopeId,
    // Allowlist rules win by precedence + low priority; IP rules are edge-preferred, access both planes.
    planes: isIp && !isAllow ? ['edge'] : ['edge', 'wp'],
    priority: isAllow ? 1 : 100,
    action: isAllow ? { kind: 'allow' } : isIp ? { kind: 'block' } : { kind: 'block', status: 403 },
    match,
    meta: { source: 'manual', description: input.description?.trim() || undefined },
  };
}

/**
 * Flip a rule's kill switch. Re-posting the result through POST /v1/console/rules upserts it
 * (version bumped so the edit is ordered) and auto-distributes, so the kill reaches the edge
 * on the same call.
 */
export function toggleRule(rule: Rule): Rule {
  return { ...rule, enabled: !rule.enabled, version: rule.version + 1 };
}

/**
 * A one-line human summary of what a rule's action actually does.
 *
 * The list used to render `action.kind` alone, which for a rate limit read as the bare word
 * `rate_limit` — the operator could see that a limit existed but not what it was, and the numbers
 * are the whole meaning of the rule. Anything that changes behaviour belongs on the screen.
 */
export function describeAction(action: Rule['action']): string {
  switch (action.kind) {
    case 'rate_limit': {
      const perSource =
        action.ipLimit === undefined
          ? 'no IP ceiling'
          : `${action.ipLimit}/${action.windowSec}s IP`;
      return `rate_limit ${action.limit}/${action.windowSec}s account · ${perSource} → ${action.then ?? 'block'}`;
    }
    case 'block':
      return action.status ? `block ${action.status}` : 'block';
    case 'challenge':
      return action.method ? `challenge (${action.method})` : 'challenge';
    default:
      return action.kind;
  }
}

/** A one-line human summary of a rule's match tree (first few leaf conditions). */
export function summarizeMatch(node: MatchNode): string {
  if ('all' in node) return node.all.map(summarizeMatch).join(' AND ');
  if ('any' in node) return node.any.map(summarizeMatch).join(' OR ');
  if ('not' in node) return `NOT ${summarizeMatch(node.not)}`;
  const value = Array.isArray(node.value) ? node.value.join(', ') : String(node.value ?? '');
  return `${node.field} ${node.op} ${value}`;
}
